import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideAlignCenter,
  lucideAlignCenterHorizontal,
  lucideAlignCenterVertical,
  lucideAlignEndHorizontal,
  lucideAlignEndVertical,
  lucideAlignHorizontalSpaceBetween,
  lucideAlignLeft,
  lucideAlignRight,
  lucideAlignStartHorizontal,
  lucideAlignStartVertical,
  lucideAlignVerticalSpaceBetween,
  lucideBold,
  lucideCheck,
  lucideChevronDown,
  lucideGroup,
  lucideCopy,
  lucideItalic,
  lucideTrash2,
  lucideUngroup,
} from '@ng-icons/lucide';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmLabelImports } from '@spartan-ng/helm/label';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import {
  CatalogEntry,
  CatalogFont,
  DesignCatalog,
  DesignElement,
  TextAlign,
} from '../../../../services/voucher-design.service';
import { AlignMode, DistributeAxis } from '../../editor-geometry';
import { PatchScope, SelectionPatch } from '../../voucher-design-editor.store';
import { ColorFieldComponent } from '../color-field/color-field.component';

export const ALIGN_BUTTONS: { mode: AlignMode; icon: string; label: string }[] = [
  { mode: 'left', icon: 'lucideAlignStartVertical', label: 'Linksbündig' },
  { mode: 'center', icon: 'lucideAlignCenterVertical', label: 'Horizontal zentrieren' },
  { mode: 'right', icon: 'lucideAlignEndVertical', label: 'Rechtsbündig' },
  { mode: 'top', icon: 'lucideAlignStartHorizontal', label: 'Oben ausrichten' },
  { mode: 'middle', icon: 'lucideAlignCenterHorizontal', label: 'Vertikal zentrieren' },
  { mode: 'bottom', icon: 'lucideAlignEndHorizontal', label: 'Unten ausrichten' },
];

export const ALIGN_ICONS = {
  lucideAlignCenterHorizontal,
  lucideAlignCenterVertical,
  lucideAlignEndHorizontal,
  lucideAlignEndVertical,
  lucideAlignStartHorizontal,
  lucideAlignStartVertical,
};

/** The one value all elements share, or null when they differ ("Gemischt"). */
function common<T>(values: T[]): T | null {
  if (values.length === 0) return null;
  return values.every((v) => v === values[0]) ? values[0] : null;
}

/**
 * Several elements selected: shared properties for the ones they apply to (the
 * font for the texts, the frame for pictures and boxes, the colours for QR codes),
 * then align, distribute, group or remove them. The buttons follow the slide
 * editors everyone knows.
 */
@Component({
  selector: 'app-selection-panel',
  standalone: true,
  imports: [
    NgIcon,
    HlmButtonImports,
    HlmCardImports,
    HlmDropdownMenuImports,
    HlmInputImports,
    HlmLabelImports,
    HlmTooltipImports,
    HlmTypographyImports,
    ColorFieldComponent,
  ],
  providers: [
    provideIcons({
      ...ALIGN_ICONS,
      lucideAlignCenter,
      lucideAlignHorizontalSpaceBetween,
      lucideAlignLeft,
      lucideAlignRight,
      lucideAlignVerticalSpaceBetween,
      lucideBold,
      lucideCheck,
      lucideChevronDown,
      lucideGroup,
      lucideCopy,
      lucideItalic,
      lucideTrash2,
      lucideUngroup,
    }),
  ],
  templateUrl: './selection-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectionPanelComponent {
  readonly count = input(0);
  /** The selection is exactly one group. */
  readonly isGroup = input(false);
  /** Some of the selected elements belong to a group. */
  readonly hasGroups = input(false);
  readonly elements = input<DesignElement[]>([]);
  readonly catalog = input<DesignCatalog | null>(null);

  readonly align = output<AlignMode>();
  readonly distribute = output<DistributeAxis>();
  readonly group = output<void>();
  readonly ungroup = output<void>();
  readonly remove = output<void>();
  /** The selection once more, next to itself (⌘D). */
  readonly duplicate = output<void>();
  /** A shared change for the selected elements of one scope. */
  readonly patchScope = output<{ scope: PatchScope; patch: SelectionPatch }>();

  readonly alignButtons = ALIGN_BUTTONS;
  readonly aligns: { value: TextAlign; icon: string; label: string }[] = [
    { value: 'left', icon: 'lucideAlignLeft', label: 'Linksbündig' },
    { value: 'center', icon: 'lucideAlignCenter', label: 'Zentriert' },
    { value: 'right', icon: 'lucideAlignRight', label: 'Rechtsbündig' },
  ];

  private readonly byKind = computed(
    () => new Map((this.catalog()?.elements ?? []).map((e) => [e.kind, e])),
  );
  readonly fonts = computed<CatalogFont[]>(() => this.catalog()?.fonts ?? []);
  readonly limits = computed(() => this.catalog()?.limits ?? null);

  private entryOf(e: DesignElement): CatalogEntry | undefined {
    return this.byKind().get(e.kind);
  }

  readonly textElements = computed(() =>
    this.elements().filter((e) => e.font && this.entryOf(e)?.render === 'text'),
  );
  readonly frameElements = computed(() =>
    this.elements().filter((e) => (this.entryOf(e)?.properties ?? []).includes('border')),
  );
  readonly boxElements = computed(() =>
    this.elements().filter((e) => this.entryOf(e)?.render === 'box'),
  );
  readonly opacityElements = computed(() =>
    this.elements().filter((e) => (this.entryOf(e)?.properties ?? []).includes('opacity')),
  );
  readonly commonOpacity = computed(() =>
    common(this.opacityElements().map((e) => Math.round((e.opacity ?? 1) * 100))),
  );

  onOpacity(event: Event) {
    const value = parseInt((event.target as HTMLInputElement).value, 10);
    if (Number.isFinite(value))
      this.emit('opacity', { opacity: Math.min(1, Math.max(0.1, value / 100)) });
  }

  readonly qrElements = computed(() =>
    this.elements().filter((e) => this.entryOf(e)?.render === 'qr'),
  );

  readonly commonFamily = computed(() => common(this.textElements().map((e) => e.font!.family)));
  readonly commonSize = computed(() => common(this.textElements().map((e) => e.font!.size)));
  readonly commonColor = computed(() => common(this.textElements().map((e) => e.font!.color)));
  readonly commonAlign = computed(() => common(this.textElements().map((e) => e.align)));
  readonly allBold = computed(() => this.textElements().every((e) => e.font!.bold));
  readonly someBold = computed(() => this.textElements().some((e) => e.font!.bold));
  readonly allItalic = computed(() => this.textElements().every((e) => e.font!.italic));
  readonly someItalic = computed(() => this.textElements().some((e) => e.font!.italic));
  readonly commonBorderWidth = computed(() =>
    common(this.frameElements().map((e) => e.border_width ?? 0)),
  );
  readonly commonBorderColor = computed(() =>
    common(this.frameElements().map((e) => e.border_color ?? '#ffffff')),
  );
  readonly commonRadius = computed(() =>
    common(this.frameElements().map((e) => e.border_radius ?? 0)),
  );
  readonly commonFill = computed(() => common(this.boxElements().map((e) => e.fill ?? null)));
  readonly commonQrColor = computed(() =>
    common(this.qrElements().map((e) => e.qr_color ?? '#000000')),
  );
  readonly commonQrBackground = computed(() =>
    common(this.qrElements().map((e) => e.qr_background ?? '#ffffff')),
  );

  cssOf(family: string): string {
    return this.fonts().find((f) => f.family === family)?.css ?? 'sans-serif';
  }

  labelOf(family: string): string {
    return this.fonts().find((f) => f.family === family)?.label ?? family;
  }

  /** "gilt für 2 von 5 Elementen" when a scope covers only part of the selection. */
  scopeNote(n: number): string | null {
    return n < this.count() ? `gilt für ${n} von ${this.count()} Elementen` : null;
  }

  emit(scope: PatchScope, patch: SelectionPatch) {
    this.patchScope.emit({ scope, patch });
  }

  onSize(event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isFinite(value) && value > 0) this.emit('text', { font: { size: value } });
  }

  onBorderWidth(event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isFinite(value) && value >= 0) this.emit('frame', { border_width: value });
  }

  onRadius(event: Event) {
    const value = parseFloat((event.target as HTMLInputElement).value);
    if (Number.isFinite(value) && value >= 0) this.emit('frame', { border_radius: value });
  }
}
